<script setup lang="ts">
import type { TenantContext } from '~/types/types'

const props = defineProps<{
  tenant: TenantContext
}>()

const socials = computed(() => props.tenant.socials || [])
const year = new Date().getFullYear()

const { t } = useI18n()
const tenantStore = useTenant()
const isMember = computed(() => tenantStore.membership.value?.status === 'active')

const onJoinClick = () => {
  const cta = document.querySelector<HTMLElement>('[data-tenant-join-cta]')
  if (cta) {
    cta.scrollIntoView({ behavior: 'smooth', block: 'center' })
    cta.click()
  }
}

const tenantStyle = computed(() => ({
  '--tenant-footer-primary': props.tenant.branding.primary_color,
  '--tenant-footer-secondary': props.tenant.branding.secondary_color,
  '--tenant-footer-accent': props.tenant.branding.accent_color
}))
</script>

<template>
  <footer
    class="relative overflow-hidden border-t border-default/70"
    :style="tenantStyle"
  >
    <div class="pointer-events-none absolute inset-0 bg-gradient-to-br from-[var(--tenant-footer-primary)]/20 via-default/60 to-[var(--tenant-footer-secondary)]/20" />
    <div class="pointer-events-none absolute -left-14 top-2 h-48 w-48 rounded-full bg-[var(--tenant-footer-primary)]/25 blur-3xl" />
    <div class="pointer-events-none absolute -right-14 bottom-0 h-48 w-48 rounded-full bg-[var(--tenant-footer-secondary)]/25 blur-3xl" />

    <div class="relative mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div class="grid gap-8 md:grid-cols-[1.3fr_1fr]">
        <div class="space-y-3">
          <div class="flex items-center gap-3">
            <img
              :src="tenant.branding.logo_url || '/images/logo.png'"
              alt="Tenant logo"
              class="ui-surface-card h-11 w-11 rounded-lg object-contain p-1"
            >
            <div>
              <p class="font-semibold">
                {{ tenant.name }}
              </p>
              <p class="text-xs ui-text-subtle">
                {{ tenant.domain }}
              </p>
            </div>
          </div>

          <p class="max-w-md text-sm ui-text-subtle">
            {{ tenant.landing.hero_subheadline || tenant.description }}
          </p>

          <div class="flex flex-wrap gap-2">
            <span class="rounded-full border border-default/70 bg-default/70 px-3 py-1 text-xs ui-text-subtle">
              {{ tenant.member_count?.toLocaleString('en-US') || '0' }} {{ t('message.members_count') }}
            </span>
            <button
              v-if="!isMember"
              type="button"
              class="cursor-pointer rounded-full border border-default/70 bg-default/70 px-3 py-1 text-xs ui-text-subtle transition hover:-translate-y-0.5 hover:ui-text-strong"
              @click="onJoinClick"
            >
              {{ t('button.join') }}
            </button>
          </div>
        </div>

        <div>
          <p class="mb-3 text-sm font-semibold">
            {{ t('label.social') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <a
              v-for="item in socials"
              :key="item.label"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 rounded-md border border-default/70 bg-default/70 px-3 py-2 text-xs ui-text-subtle transition hover:-translate-y-0.5 hover:ui-text-strong"
            >
              <UIcon
                :name="item.icon"
                class="size-3.5"
              />
              {{ item.label }}
            </a>
          </div>
          <div class="mt-4 rounded-lg border border-default/70 bg-default/70 p-3 text-xs ui-text-subtle">
            {{ t('message.follow_for_updates') }}
          </div>
        </div>
      </div>

      <div class="mt-8 border-t border-default/70 pt-4 text-xs ui-text-subtle">
        © {{ year }} {{ tenant.name }} Community. All rights reserved.
      </div>
    </div>
  </footer>
</template>
